import { createContext, useContext } from 'react';

import type { EditorContentUnifiedQuery$data } from './__generated__/EditorContentUnifiedQuery.graphql.ts';

export type EditorContentNode = NonNullable<
	NonNullable<NonNullable<EditorContentUnifiedQuery$data['content']>['nodes']>[number]
>;

// The query selects only aliased fragment spreads under the node, so its keys are the fragments.
export type EditorContentFragmentKey = keyof EditorContentNode;

// isLoading: ref is still the previous document's while the next document loads.
export type EditorContentFragment<K extends EditorContentFragmentKey> =
	| { isProvided: true; ref: EditorContentNode[K] | null; isLoading: boolean }
	| { isProvided: false };

export type EditorContentFragments = {
	readonly [K in EditorContentFragmentKey]: EditorContentFragment<K>;
};

// Every fragment of EditorContentUnifiedQuery; adding a fragment to the query without a key here
// fails to compile.
const EDITOR_CONTENT_FRAGMENT_KEYS = Object.keys({
	contentTypesHeaderFragment: true,
} as const satisfies Record<EditorContentFragmentKey, true>) as EditorContentFragmentKey[];

function mapFragments(
	toFragment: (key: EditorContentFragmentKey) => EditorContentFragment<EditorContentFragmentKey>,
): EditorContentFragments {
	return Object.fromEntries(
		EDITOR_CONTENT_FRAGMENT_KEYS.map((key) => [key, toFragment(key)]),
	) as EditorContentFragments;
}

/**
 * The fragments EditorContentUnifiedQuery provides for a node, every one of them provided even when
 * the operation returns no node, so its readers render no data instead of running their queries.
 */
export function createEditorContentFragments(
	node: EditorContentNode | null | undefined,
	isLoading: boolean,
): EditorContentFragments {
	return mapFragments((key) => ({ isProvided: true, ref: node?.[key] ?? null, isLoading }));
}

const NOT_PROVIDED_FRAGMENTS: EditorContentFragments = mapFragments(() => ({ isProvided: false }));

export const EditorContentFragmentsContext: React.Context<EditorContentFragments> =
	createContext<EditorContentFragments>(NOT_PROVIDED_FRAGMENTS);

/**
 * The fragments of the editor document. One is not provided outside EditorContentContainer
 * (embedded, template and smart create editors, renderers) and with the experiment off, where its
 * reader keeps its own query; when provided, ref is null if the operation returns no node.
 */
export function useEditorContentFragments(): EditorContentFragments {
	return useContext(EditorContentFragmentsContext);
}
